import React from "react";
import Icon from "@/components/Icon";
import { Field, Input, TextArea, FileButton } from "@/components/DestinationForm";
import SocialSelector from "@/components/SocialSelector";

const PREFIXES = ["", "Mr.", "Ms.", "Mrs.", "Dr.", "Prof.", "Er.", "Miss"];

// Priority workflow: employee contact vCard builder.
export default function VCardForm({ data, onChange, vcardMode, setVcardMode }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const fullName = [data.prefix, data.first_name, data.last_name].filter(Boolean).join(" ");

  return (
    <div className="space-y-5">
      {/* Mode chooser */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { id: "direct", title: "Direct Contact QR", desc: "vCard encoded in the image. Native contact import. Cannot be edited after printing.", icon: "Download" },
          { id: "updateable", title: "Updateable QR", desc: "Permanent link serves the current .vcf. Edit anytime — printed codes keep working.", icon: "RefreshCw" },
        ].map((m) => (
          <button key={m.id} data-testid={`vcard-mode-${m.id}`} onClick={() => setVcardMode(m.id)}
            className={`text-left rounded-2xl border-2 p-4 transition-colors ${vcardMode === m.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
            <Icon name={m.icon} className={`h-5 w-5 mb-2 ${vcardMode === m.id ? "text-primary" : "text-muted-foreground"}`} />
            <div className="font-semibold text-sm">{m.title}</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{m.desc}</p>
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-accent/50 border border-border p-3 text-xs text-muted-foreground flex gap-2">
        <Icon name="Info" className="h-4 w-4 shrink-0 mt-0.5" />
        {vcardMode === "direct"
          ? "Editing the employee later creates a new QR image and cannot update copies already printed. Adding many long links makes the QR denser and slower to scan. A separate downloadable photo .vcf is provided on the scan page."
          : "Editing the employee updates the file served at the same URL — previously printed QRs keep working. Contacts already saved by others will not update automatically. iPhone opens the .vcf directly; Android may download it first."}
      </div>

      {/* Name preview */}
      <div className="rounded-xl border border-border bg-card p-3">
        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Live full name</div>
        <div className="text-lg font-display font-bold" data-testid="vcard-name-preview">{fullName || "Your name preview"}</div>
      </div>

      <div className="grid grid-cols-[90px_1fr_1fr] gap-3">
        <Field label="Prefix"><select value={data.prefix || ""} onChange={(e) => set("prefix", e.target.value)} data-testid="vc-prefix" className="w-full rounded-xl border border-input bg-background px-2 py-2.5 text-sm">{PREFIXES.map((p) => <option key={p} value={p}>{p || "—"}</option>)}</select></Field>
        <Field label="First name"><Input value={data.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="vc-first" /></Field>
        <Field label="Last name"><Input value={data.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="vc-last" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Job title"><Input value={data.job_title || ""} onChange={(e) => set("job_title", e.target.value)} data-testid="vc-title" /></Field>
        <Field label="Company"><Input value={data.company || ""} onChange={(e) => set("company", e.target.value)} data-testid="vc-company" /></Field>
      </div>

      <Field label="Profile photo" hint={vcardMode === "direct" ? "Photo may not embed reliably in a direct QR — a photo .vcf is offered on scan." : "Included in the vCard where supported."}>
        <div className="flex items-center gap-3">
          {data.photo && <img src={data.photo} alt="" className="h-16 w-16 rounded-full object-cover" />}
          <div className="flex-1"><FileButton accept="image/*" label="Upload photo" testid="vc-photo" onUrl={(url) => set("photo", url)} /></div>
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Mobile"><Input placeholder="+1 555 000 1234" value={data.mobile || ""} onChange={(e) => set("mobile", e.target.value)} data-testid="vc-mobile" /></Field>
        <Field label="Office phone"><Input value={data.office_phone || ""} onChange={(e) => set("office_phone", e.target.value)} data-testid="vc-office" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email"><Input value={data.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="vc-email" /></Field>
        <Field label="Website"><Input value={data.website || ""} onChange={(e) => set("website", e.target.value)} data-testid="vc-website" /></Field>
      </div>
      <div className="pt-1"><SocialSelector value={data.social_links || []} onChange={(v) => set("social_links", v)} style={data.social_style} onStyle={(s) => set("social_style", s)} /></div>
      <Field label="WhatsApp">
        <div className="flex gap-2">
          <Input placeholder="Number or wa.me link" value={data.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="vc-whatsapp" />
          <button data-testid="vc-use-mobile" onClick={() => set("whatsapp", `https://wa.me/${(data.mobile || "").replace(/[^0-9]/g, "")}`)} className="shrink-0 rounded-xl border border-border px-3 text-xs font-semibold hover:bg-accent whitespace-nowrap">Use mobile</button>
        </div>
      </Field>
      <Field label="Google Maps location"><Input placeholder="https://maps.google.com/..." value={data.maps || ""} onChange={(e) => set("maps", e.target.value)} data-testid="vc-maps" /></Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Street"><Input value={data.street || ""} onChange={(e) => set("street", e.target.value)} data-testid="vc-street" /></Field>
        <Field label="City"><Input value={data.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="vc-city" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Region"><Input value={data.region || ""} onChange={(e) => set("region", e.target.value)} data-testid="vc-region" /></Field>
        <Field label="Postcode"><Input value={data.postcode || ""} onChange={(e) => set("postcode", e.target.value)} data-testid="vc-postcode" /></Field>
        <Field label="Country"><Input value={data.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="vc-country" /></Field>
      </div>
      <Field label="Notes"><TextArea rows={2} value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="vc-notes" /></Field>
    </div>
  );
}
